/**
 * Flex rows drop `display:none` cells, so a hidden caption slides the field left.
 * Keep a column spacer while any sibling in the row is still visible.
 */

export type HiddenColumnSpacerItem = {
  element?: { _attributes?: { id?: string | number } } | null
  _attributes?: Record<string, unknown>
  _isRowspanPlaceholder?: boolean
  _isColspanPlaceholder?: boolean
  _isGridColumnSpacer?: boolean
  _isHiddenColumnSpacer?: boolean
}

function isStructuralPlaceholder(item: HiddenColumnSpacerItem | undefined): boolean {
  if (!item) return true
  return Boolean(
    item._isRowspanPlaceholder ||
      item._isColspanPlaceholder ||
      item._isGridColumnSpacer ||
      item._isHiddenColumnSpacer,
  )
}

function layoutElementId(item: HiddenColumnSpacerItem): string {
  const raw = item?.element?._attributes?.id
  return raw == null ? '' : String(raw).trim()
}

function isShellHidden(
  item: HiddenColumnSpacerItem,
  shellVisibleByElementId?: Record<string, boolean> | null,
): boolean {
  const id = layoutElementId(item)
  if (!id) return false
  return shellVisibleByElementId?.[id] === false
}

export function applyHiddenElementColumnSpacers<T extends HiddenColumnSpacerItem>(
  items: T[] | null | undefined,
  shellVisibleByElementId?: Record<string, boolean> | null,
): { items: T[]; hideRow: boolean } {
  if (!Array.isArray(items) || items.length === 0) {
    return { items: items ?? [], hideRow: false }
  }

  const elementItems = items.filter((item) => item?.element && !isStructuralPlaceholder(item))
  if (elementItems.length === 0) {
    return { items, hideRow: false }
  }

  const hidden = elementItems.map((item) => isShellHidden(item, shellVisibleByElementId))
  if (hidden.every(Boolean)) {
    return { items, hideRow: true }
  }
  if (!hidden.some(Boolean)) {
    return { items, hideRow: false }
  }

  const next = items.map((item) => {
    if (!item?.element || isStructuralPlaceholder(item) || !isShellHidden(item, shellVisibleByElementId)) {
      return item
    }
    return {
      ...item,
      element: undefined,
      _isHiddenColumnSpacer: true,
      _attributes: { ...(item._attributes || {}) },
    }
  })
  return { items: next, hideRow: false }
}
